import styled from 'styled-components';
import { Link } from '../Link/Link';
import { ButtonLink } from '../ButtonLink/ButtonLink';
import { useAuth } from 'react-oidc-context';
import { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaUser } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { selectApplicationProfile } from '../../redux/selectors/profileSelectors';

const StyledHeader = styled.header`
  background: #fff;
  box-shadow: 0 1px 0 0 rgba(139, 141, 157, 0.05),
    0 5px 10px 0 rgba(65, 71, 108, 0.15);
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10;
  padding: 0 30px;
`;

const LogoSvg = styled.img`
  max-width: 350px;
  width: 100%;
`;

const StyledButton = styled.button`
  border: none;
  align-items: center;
  background-color: #fcb12d;
  border-radius: 9999px;
  color: #fff;
  display: flex;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 1px;
  height: 45px;
  width: 45px;
  justify-content: center;
`;

const StyledUserMenu = styled.div`
  position: relative;
`;

const StyledDropdown = styled.ul`
  background: #fff;
  box-shadow: 0 1px 0 0 rgba(139, 141, 157, 0.05),
    0 5px 10px 0 rgba(65, 71, 108, 0.15);
  padding: 0 !important;
  position: absolute;
  border-radius: 12px;
  top: 30px;
  right: 0;
  z-index: 3;
`;

const StyledItem = styled.li`
  color: rgb(95, 100, 121);
  font-size: 14px;
  padding: 15px;
  list-style: none;
  transition: all 0.2s ease-out;
  width: 150px;

  &:hover {
    color: #fcb12d !important;
    cursor: pointer;
  }
`;

const UserInfoItem = styled.li`
  color: rgb(95, 100, 121);
  font-size: 14px;
  padding: 12px 15px 8px 15px;
  list-style: none;
  width: 200px;
  border-bottom: 1px solid #e9ecef;
`;

const TruncatedText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const Header = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const applicationProfile = useSelector(selectApplicationProfile);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const appFirstName = applicationProfile?.firstName || '';
  const appLastName = applicationProfile?.lastName || '';
  const appFullName = `${appFirstName} ${appLastName}`.trim();
  const appDisplayName =
    appFullName || '';

  const givenName = auth?.user?.profile?.given_name || '';
  const familyName = auth?.user?.profile?.family_name || '';
  const fullName = `${givenName} ${familyName}`.trim();
  const oidcDisplayName =
    fullName ||
    auth?.user?.profile?.name ||
    auth?.user?.profile?.email ||
    auth?.user?.profile?.['cognito:username'] ||
    '';

  const displayName = appDisplayName || oidcDisplayName || '';

  const userEmail =
    auth?.user?.profile?.email || applicationProfile?.contextureEmailAddress;

  const getInitials = (name) => {
    if (!name) return '';

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first}${last}`;
  };

  const initials = applicationProfile ? getInitials(appDisplayName || oidcDisplayName) : '';

  return (
    <StyledHeader>
      <div className='row g-0'>
        <Col lg={12} className='py-3'>
          <div className='d-flex align-items-center justify-content-between'>
            <Link to='/' minimal>
              <LogoSvg
                src='https://securitybulldog.com/wp-content/uploads/2022/01/security_bulldog_logo_horz.svg'
                alt='Security bulldog logo'
              />
            </Link>

            <nav className='d-md-block d-none'>
              <Link
                to='https://securitybulldog.com/about'
                label='About'
                external
              />
              <Link
                to='https://securitybulldog.com/blog/'
                label='Blog'
                external
              />
              <Link
                to='https://securitybulldog.com/contact/'
                label='Contact'
                external
              />
            </nav>

            <div className='flex justify-content-end'>
              {auth.isAuthenticated && (
                <OutsideClickHandler
                  onOutsideClick={() => setIsDropdownVisible(false)}
                >
                  <StyledUserMenu>
                    <StyledButton
                      onClick={() => setIsDropdownVisible(true)}
                      data-testid='user-profile-button'
                    >
                        {initials || <FaUser />}
                    </StyledButton>

                    {isDropdownVisible && (
                      <StyledDropdown>
                        <UserInfoItem>
                        <TruncatedText className='fw-bold' title={displayName}>
                          {displayName}
                        </TruncatedText>
                          {userEmail && (
                            <div style={{ fontSize: '12px', marginTop: '2px' }}>
                              {userEmail}
                            </div>
                          )}
                        </UserInfoItem>
                        <StyledItem
                          onClick={() => {
                            navigate('/profile');
                          }}
                        >
                          Profile
                        </StyledItem>
                        <StyledItem
                          style={{paddingTop: '0px'}}
                          onClick={() => {
                            void auth.signoutRedirect({
                              extraQueryParams: {
                                client_id:
                                  process.env.REACT_APP_COGNITO_CLIENT_ID,
                                // Cognito expects `logout_uri` on /logout
                                logout_uri:
                                  process.env.REACT_APP_COGNITO_REDIRECT_URI,
                              },
                            });
                          }}
                        >
                          Sign Out
                        </StyledItem>
                      </StyledDropdown>
                    )}
                  </StyledUserMenu>
                </OutsideClickHandler>
              )}

              {!auth.isAuthenticated && (
                <>
                  <ButtonLink
                    label='Sign in'
                    variant='light'
                    onClick={() => auth.signinRedirect()}
                  />

                  <ButtonLink to='/' label='Sign up' variant='primary' />

                  <ButtonLink
                    to='/'
                    label='Request a demo'
                    variant='secondary'
                    external
                  />
                </>
              )}
            </div>
          </div>
        </Col>
      </div>
    </StyledHeader>
  );
};
